import { z } from "zod";

// Base schema for API key creation
export const createApiKeySchema = z.object({
  name: z
    .string({
      required_error: "API key name is required",
    })
    .min(1, "API key name cannot be empty")
    .max(100, "API key name cannot exceed 100 characters"),
  expiresAt: z.string().datetime().optional().nullable(),
  scopes: z.array(z.string()).optional(),
});

// Use z.input for the input type (what the caller provides)
export type ZodCreateApiKeySchema = z.input<typeof createApiKeySchema>;

// Schema for revoking an API key
export const revokeApiKeySchema = z.object({
  id: z.string().uuid({ message: "Invalid API key ID" }),
});

export type ZodRevokeApiKeySchema = z.infer<typeof revokeApiKeySchema>;

// Schema for getting an API key by ID
export const getApiKeySchema = z.object({
  id: z.string().uuid({ message: "Invalid API key ID" }),
});

export type ZodGetApiKeySchema = z.infer<typeof getApiKeySchema>;

// Schema for listing API keys
export const listApiKeysSchema = z.object({
  limit: z.number().positive().optional().default(50),
  offset: z.number().nonnegative().optional().default(0),
});

export type ZodListApiKeysSchema = z.infer<typeof listApiKeysSchema>;

// Full API key schema (for database records, without the actual key)
export const apiKeySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(), // First 8 chars for identification (bn_xxxx)
  lastUsedAt: z.date().nullable(),
  expiresAt: z.date().nullable(),
  scopes: z.array(z.string()),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  createdAt: z.date(),
  revokedAt: z.date().nullable(),
});

export type ApiKeySchema = z.infer<typeof apiKeySchema>;

// Response when creating an API key (includes the plaintext key - only shown once)
export const createApiKeyResponseSchema = z.object({
  apiKey: z.string(), // The actual API key (bn_xxxxx...) - only returned on creation
  id: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  expiresAt: z.date().nullable(),
  scopes: z.array(z.string()),
  createdAt: z.date(),
});

export type CreateApiKeyResponseSchema = z.infer<typeof createApiKeyResponseSchema>;

// API key list item (masked, no actual key)
export const apiKeyListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  lastUsedAt: z.date().nullable(),
  expiresAt: z.date().nullable(),
  scopes: z.array(z.string()),
  createdAt: z.date(),
  revokedAt: z.date().nullable(),
  isExpired: z.boolean(),
  isRevoked: z.boolean(),
});

export type ApiKeyListItemSchema = z.infer<typeof apiKeyListItemSchema>;
